import { AddRounded, ClearRounded, CloseRounded, Delete, DeleteRounded, DescriptionRounded, Download, FilterListRounded, ImageRounded, PictureAsPdf, SearchRounded, UploadFile, UploadRounded } from '@mui/icons-material'
import { LoadingButton } from '@mui/lab'
import { Box, Button, Checkbox, Drawer, FormControlLabel, FormGroup, IconButton, InputAdornment, LinearProgress, Menu, Modal, Paper, Skeleton, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography } from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import ApiClient from '../../backend/ApiClient'
import API_URLS from '../../backend/ApiUrls'
import { COMPILED_CONSTANTS } from '../../CompiledConstants'
import { IMedia } from '../../definitions/Media'
import { IPreviewURI, IReport } from '../../definitions/report_builder/ReportBuilderDefinitions'
import Formatter from '../../helpers/Formatter'
import Helper from '../../helpers/Helper'
import UploadManager from '../../managers/UploadManager'
import FileUpload from '../controls/FileUpload'
import ImageViewer from '../controls/ImageViewer'
import useCommand from '../hooks/useCommand'
import useInvalidate from '../hooks/useInvalidate'
import useQuery, { buildQueryKey } from '../hooks/useQuery'
import { modals } from '../services/ModalService'
import { snackbar } from '../services/SnackbarService'

export default function MediaManager() {
    const [selectedFiles, setSelectedFiles] = useState<File[]>([])
    const [selectedMedia, setSelectedMedia] = useState<IMedia>(null)
    const [modalStatus] = useState<'insert' | 'loading' | 'success' | 'failed'>('insert')
    const [search, setSearch] = useState<string>('')
    const [filterType, setFilterType] = useState({ pdf: false, doc: false, image: false })
    const [openUpload, setOpenUpload] = useState<boolean>(false)
    const [description, setDescription] = useState<string>('')
    const [dimensions, setDimensions] = useState<string>('')
    const [anchorEl, setAnchorEl] = useState<HTMLElement>(null)
    const [openDrawer, setOpenDrawer] = useState<boolean>(false)
    const [mediaResponse, setMediaResponse] = useState<IPreviewURI>(null)
    const [imageLoading, setImageLoading] = useState(false)

    const open = Boolean(anchorEl)

    const invalidate = useInvalidate({ queryKey: buildQueryKey(API_URLS.os.getAllMedia + '/*') })

    const { response } = useQuery<Array<IReport & { username: string }>>({
        queryKey: buildQueryKey('reports'),
        queryFn: () => ApiClient.getReports(),
        cache: true
    })

    const { execute: uploadFile, status: statusUpload } = useCommand({
        commandFn: async () => {
            if (Helper.hasVal(selectedFiles)) {
                const resultUpload = []

                for (let i = 0; i < selectedFiles.length; i++) {
                    const selectedFile = selectedFiles[i]
                    try {
                        await UploadManager.uploadFile(selectedFile, description, dimensions)
                        resultUpload.push(true)
                    } catch {
                        resultUpload.push(false)
                    }
                }

                setOpenUpload(false)

                if (resultUpload.every(x => x)) snackbar.show({ severity: 'success', text: `Files have been uploaded successfully.` })
                else if (resultUpload.every(x => !x)) snackbar.show({ severity: 'error', text: `Files upload failed.` })
                else snackbar.show({ severity: 'warning', text: 'Some files may not have been uploaded correctly.' })
            }
        },
        onFinish: () => invalidate()

    })

    const { response: allMediaRes, status: allMediaStatus } = useQuery({
        queryKey: buildQueryKey(API_URLS.os.getAllMedia),
        queryFn: () => ApiClient.getAllMedia(),
        cache: true
    })

    const readFile = async (fileKey: string) => {
        try {
            setImageLoading(true)
            const response = await ApiClient.readMediaItem(fileKey, COMPILED_CONSTANTS.evolvemediaBucketName)
            setMediaResponse(response)
            setImageLoading(false)
        } catch (error) {
            console.error('ERROR: ', error)
            snackbar.show({ severity: error, text: 'Error during read file' })
        }

        setImageLoading(false)
    }

    const handleDelete = async (file: IMedia) => {
        modals.openConfirm({
            title: 'Confirm File Deletion',
            children: <p>Are you sure you want to delete this file?</p>,
            labels: { confirm: 'Delete', cancel: 'Cancel' },
            colors: { confirm: 'error', cancel: 'primary' },
            onConfirm: async () => {
                try {
                    if (file.type === 'image') {
                        const imgIsUsed = response.some(report =>
                            report.sections.some(section =>
                                section.columns.some(column =>
                                    column.type === 'image' &&
                                    (column.settings.fileKey === file.filekey)
                                )
                            )
                        )
                        if (imgIsUsed) {
                            snackbar.show({ severity: 'warning', text: `File ${file.name} is used in a Report and cannot be deleted` })
                            return
                        }
                    }
                    await ApiClient.deleteMediaFile(file.filekey, COMPILED_CONSTANTS.evolvemediaBucketName, file.uuid)
                    setOpenDrawer(false)
                    invalidate()
                    snackbar.show({ severity: 'success', text: `File ${file.name} deleted successfully` })
                } catch (error) {
                    snackbar.show({ severity: 'error', text: `Unable to delete file ${file.name}` })
                    console.error(error)
                    return
                }
            }
        })
    }

    const downloadFile = async (key: string, bucketName: string, fileName: string) => {
        const result = await ApiClient.readMediaItem(key, bucketName)

        const response = await fetch(result.url)
        const blob = await response.blob()
        const url = window.URL.createObjectURL(blob)

        const a = document.createElement('a')
        a.href = url
        a.download = fileName ?? 'file'
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        window.URL.revokeObjectURL(url)
    }

    const handleFilterType = (e: any) => {
        const { name } = e.target
        setFilterType({ ...filterType, [name]: !filterType[name] })
    }

    const renderLoading = () => {
        return (
            <>
                {[...Array(5)].map((_, index) => (
                    <TableRow key={index}>
                        <TableCell>
                            <Stack direction='row' spacing={1} alignItems='center'>
                                <Skeleton variant='circular' width={40} height={40} />
                                <Skeleton variant='text' width={120} height={30} />
                            </Stack>
                        </TableCell>
                        <TableCell>
                            <Skeleton variant='text' width={60} />
                        </TableCell>
                        <TableCell>
                            <Skeleton variant='text' width={80} />
                        </TableCell>
                        <TableCell>
                            <Skeleton variant='text' width={100} />
                        </TableCell>
                        <TableCell>
                            <Stack direction='row' spacing={1}>
                                <Skeleton variant='circular' width={32} height={32} />
                                <Skeleton variant='circular' width={32} height={32} />
                            </Stack>
                        </TableCell>
                    </TableRow>
                ))}
            </>
        )
    }

    const filteredFiles = useMemo(() => {
        if (allMediaStatus !== 'success') return []
        const files = allMediaRes.filter(file => file.name.toLowerCase().includes(search.toLowerCase()))
        const activeTypes = Object.entries(filterType).filter(([, v]) => v).map(([k]) => k)
        if (activeTypes.length === 0) return files
        return files.filter(file => activeTypes.includes(file.type))
    }, [allMediaRes, search, filterType])

    useEffect(() => {
        if (!openUpload) {
            setDescription('')
        }
    })

    useEffect(() => {
        if (!selectedFiles || selectedFiles.length === 0) return

        selectedFiles.forEach(file => {
            if (file.type.startsWith('image/')) {
                const img = new Image()
                img.onload = () => {
                    setDimensions(`${img.width}x${img.height}`)
                    URL.revokeObjectURL(img.src)
                }
                img.src = URL.createObjectURL(file)
            } else return
        })
    }, [selectedFiles])

    return (
        <>
            <Stack spacing={3}>
                <Stack justifyContent='space-between' spacing={1}>
                    <Typography variant='h5'>
                        <strong>Media</strong>
                    </Typography>
                    <Typography>
                        Manage all media types like PDFs, Images, Documents...
                    </Typography>
                </Stack>

                <Stack justifyContent='space-between' direction='row' alignItems='center'>
                    <Stack flexDirection='row' alignItems='center'>
                        <Stack flexDirection='row' alignItems='center' gap={1} width={320}>
                            <TextField
                                fullWidth
                                placeholder='Search Media'
                                size='small'
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position='start'>
                                            <SearchRounded fontSize='small' />
                                        </InputAdornment>
                                    ),
                                    endAdornment: (
                                        <InputAdornment position='end'>
                                            {(search.length > 0) && (
                                                <IconButton
                                                    onClick={() => setSearch('')}
                                                    edge='end'
                                                    size='small'>
                                                    <ClearRounded fontSize='small' />
                                                </IconButton>
                                            )}
                                        </InputAdornment>
                                    )
                                }}
                            />
                            <IconButton
                                size='small'
                                onClick={(event: React.MouseEvent<HTMLButtonElement>) => setAnchorEl(event.currentTarget)}>
                                <FilterListRounded fontSize='small' />
                            </IconButton>
                            <Menu
                                id='basic-menu'
                                anchorEl={anchorEl}
                                open={open}
                                onClose={() => setAnchorEl(null)}>
                                <FormGroup>
                                    <Stack paddingLeft={2}>
                                        <Box>
                                            <FormControlLabel checked={filterType.pdf} control={<Checkbox />} label='pdf' name='pdf' onClick={handleFilterType} />
                                        </Box>
                                        <Box>
                                            <FormControlLabel checked={filterType.doc} control={<Checkbox />} label='doc' name='doc' onClick={handleFilterType} />
                                        </Box>
                                        <Box>
                                            <FormControlLabel checked={filterType.image} control={<Checkbox />} label='image' name='image' onClick={handleFilterType} />
                                        </Box>
                                    </Stack>

                                </FormGroup>
                            </Menu>
                        </Stack>
                    </Stack>
                    <Button
                        size='small'
                        onClick={() => setOpenUpload(true)}
                        startIcon={<AddRounded />}
                        variant='contained'>
                        Upload Media
                    </Button>
                </Stack>

                <Stack>
                    <TableContainer component={Paper}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>
                                        <strong>File Name</strong>
                                    </TableCell>
                                    <TableCell>
                                        <strong>File type</strong>
                                    </TableCell>
                                    <TableCell>
                                        <strong>Size</strong>
                                    </TableCell>
                                    <TableCell>
                                        <strong>Upload date</strong>
                                    </TableCell>
                                    <TableCell>
                                        <strong>Actions</strong>
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                { (allMediaStatus === 'loading' || allMediaStatus === 'stale')
                                    ? renderLoading()
                                    : filteredFiles.map(file => (
                                        <TableRow
                                            hover
                                            sx={{ cursor: 'pointer' }}
                                            onClick={() => {
                                                setSelectedMedia(file)
                                                if (file.type === 'image') readFile(file.filekey)
                                                setOpenDrawer(true)
                                            }}
                                            key={file.uuid}>
                                            <TableCell>
                                                <Stack direction='row' spacing={1} justifyContent='start' alignItems='center'>
                                                    <Stack>
                                                        {file.type === 'image' && <ImageRounded fontSize='large' />}
                                                        {file.type === 'pdf' && <PictureAsPdf fontSize='large' />}
                                                        {file.type === 'doc' && <DescriptionRounded fontSize='large' />}
                                                        {file.type === 'other' && <DescriptionRounded fontSize='large' />}
                                                        {file.type === 'text' && <DescriptionRounded fontSize='large' />}
                                                        {file.type === 'xls' && <DescriptionRounded fontSize='large' />}
                                                    </Stack>

                                                    <Stack>
                                                        {file.name}
                                                    </Stack>
                                                </Stack>
                                            </TableCell>
                                            <TableCell>{file.type}</TableCell>
                                            <TableCell>{Formatter.bytes(file.size, 1)}</TableCell>
                                            <TableCell>{new Date(file.uploadDate).toLocaleDateString()}</TableCell>
                                            <TableCell>
                                                <Stack direction='row'>
                                                    <Tooltip title='Download' arrow>
                                                        <IconButton
                                                            onClick={e => {
                                                                e.stopPropagation()
                                                                downloadFile(file.filekey, COMPILED_CONSTANTS.evolvemediaBucketName, file.name)
                                                            }}>
                                                            <Download />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip
                                                        title='Delete'
                                                        arrow>
                                                        <IconButton
                                                            color='error'
                                                            onClick={e => {
                                                                e.stopPropagation()
                                                                handleDelete(file)
                                                            }}>
                                                            <Delete />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Stack>
            </Stack>
            <Modal
                open={openUpload}
                onClose={() => setOpenUpload(false)}
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center'
                }}>
                <Paper
                    elevation={6}
                    sx={{
                        justifyContent: 'space-between',
                        padding: 2,
                        display: 'flex',
                        flexDirection: 'column',
                        width: 600,
                        height: 650
                    }}>
                    <Stack spacing={2}>
                        <Stack direction='row' justifyContent='space-between'>
                            <Typography
                                variant='h6'>Upload New File
                            </Typography>
                            <IconButton
                                onClick={() => setOpenUpload(false)}>
                                <CloseRounded />
                            </IconButton>
                        </Stack>
                        <Stack spacing={1}>
                            <Typography variant='h6'>
                                File Name
                                <span style={{ paddingLeft: 'calc(var(--unit) / 2)', color: 'var(--color-bad)', fontWeight: 'bold' }}>*</span>
                            </Typography>
                            <TextField
                                disabled
                                value={(!selectedFiles || selectedFiles.length === 0) ? '' : selectedFiles[0].name}
                            />
                        </Stack>
                        <Stack spacing={1}>
                            <Typography variant='h6'>
                                Description
                                {' '}
                                <Typography variant='caption'>(optional)</Typography>
                            </Typography>
                            <TextField
                                value={description}
                                onChange={e => setDescription(e.target.value)}
                                multiline
                                rows={4}
                                placeholder='Write a short description about the file...'
                            />
                        </Stack>
                        <Stack spacing={1}>
                            <Typography variant='h6'>
                                Upload File
                                <span style={{ paddingLeft: 'calc(var(--unit) / 2)', color: 'var(--color-bad)', fontWeight: 'bold' }}>*</span>
                            </Typography>
                            <FileUpload setSelectedFiles={setSelectedFiles} />
                            {Array.isArray(selectedFiles) && selectedFiles.length > 0 && (
                                <Box display='flex' flexDirection='row' alignItems='center' gap={2} mb={1} mt={2}>
                                    <UploadFile color='primary' />
                                    <Box flex={1}>
                                        <Typography fontWeight={500}>{selectedFiles[0].name}</Typography>
                                        <Typography variant='caption'>
                                            {Formatter.bytes(selectedFiles[0].size, 1)}
                                            {' • '}
                                            {modalStatus === 'insert'
                                                ? 'Ready to Upload'
                                                : modalStatus === 'loading'
                                                    ? 'Loading'
                                                    : modalStatus === 'success' ? 'Success' : 'Failed'}
                                        </Typography>
                                        {modalStatus === 'loading' && <LinearProgress sx={{ mt: 1 }} />}
                                    </Box>
                                </Box>
                            )}
                        </Stack>
                    </Stack>
                    <Stack>
                        <Stack flexDirection='row' justifyContent='flex-end'>
                            <LoadingButton
                                disabled={!selectedFiles || selectedFiles.length === 0}
                                loadingPosition='start'
                                loading={statusUpload === 'loading'}
                                startIcon={<UploadRounded />}
                                variant='contained'
                                onClick={uploadFile}>
                                Upload File
                            </LoadingButton>
                        </Stack>
                    </Stack>
                </Paper>
            </Modal>

            <Drawer
                open={openDrawer}
                anchor='right'
                onClose={() => {
                    setOpenDrawer(false)
                    setSelectedMedia(null)
                    setMediaResponse(null)
                }}
                PaperProps={{ sx: { width: '50%', borderRadius: 2, display: 'flex', flexDirection: 'column' } }}>
                <Stack sx={{ height: '100%' }}>
                    <Paper elevation={3} sx={{ borderBottomLeftRadius: 0, borderBottomRightRadius: 0 }}>
                        <Stack padding={3} direction='row' justifyContent='space-between'>
                            <Stack direction='row'>
                                <Stack padding={1}>
                                    { selectedMedia?.type === 'image' && <ImageRounded fontSize='large' /> }
                                    { selectedMedia?.type === 'pdf' && <PictureAsPdf fontSize='large' /> }
                                    { selectedMedia?.type === 'doc' && <DescriptionRounded fontSize='large' /> }
                                    { selectedMedia?.type === 'other' && <DescriptionRounded fontSize='large' /> }
                                    { selectedMedia?.type === 'text' && <DescriptionRounded fontSize='large' /> }
                                    { selectedMedia?.type === 'xls' && <DescriptionRounded fontSize='large' /> }
                                </Stack>
                                <Stack>
                                    <Typography variant='overline'>Media File</Typography>
                                    <Typography variant='subtitle2'>{selectedMedia?.name}</Typography>
                                </Stack>
                            </Stack>
                            <Stack>
                                <Button
                                    onClick={() => downloadFile(selectedMedia.filekey, COMPILED_CONSTANTS.evolvemediaBucketName, selectedMedia.name)}
                                    startIcon={<Download />}
                                    variant='contained'>Download File
                                </Button>
                            </Stack>
                        </Stack>
                    </Paper>
                    <Paper
                        elevation={3}
                        sx={{
                            padding: 3,
                            flex: 1,
                            overflow: 'hidden',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'space-between',
                            gap: 2,
                            borderTopLeftRadius: 0,
                            borderTopRightRadius: 0 }}>
                        <Stack spacing={2} overflow='hidden'>
                            <Stack spacing={2}>
                                <Typography variant='subtitle2'>Description</Typography>
                                <TextField value={selectedMedia?.description} disabled multiline rows={4}></TextField>
                            </Stack>
                            <Stack
                                direction='row'
                                justifyContent='space-around'
                                spacing={20}>
                                <Stack>
                                    <Typography variant='subtitle2'>Created at</Typography>
                                    <Typography variant='body2'>{new Date(selectedMedia?.uploadDate).toLocaleDateString()}</Typography>
                                </Stack>
                                <Stack>
                                    <Typography variant='subtitle2'>Size</Typography>
                                    <Typography variant='body2'>{Formatter.bytes((selectedMedia?.size), 1)}</Typography>
                                </Stack>
                                {selectedMedia?.type === 'image' && (
                                    <Stack>
                                        <Typography variant='subtitle2'>Dimensions</Typography>
                                        <Typography variant='body2'>{selectedMedia?.dimensions}</Typography>
                                    </Stack>
                                )}
                                <Stack>
                                    <Typography variant='subtitle2'>Uploaded by</Typography>
                                    <Typography variant='body2'>{selectedMedia?.uploadedBy}</Typography>
                                </Stack>
                            </Stack>
                        </Stack>
                        {selectedMedia?.type === 'image' && (
                            <Stack overflow='hidden' flex={1} alignItems='center' justifyContent='center'>
                                {imageLoading ? <Skeleton animation='wave' variant='rounded' width='50%' height='50%' /> : <ImageViewer url={mediaResponse?.url} />}
                            </Stack>
                        )}
                        <Stack direction='row' justifyContent='flex-end'>
                            <LoadingButton
                                loadingPosition='start'
                                onClick={() => handleDelete(selectedMedia)}
                                startIcon={<DeleteRounded />}
                                variant='contained'
                                color='error'>
                                Delete File
                            </LoadingButton>
                        </Stack>
                    </Paper>
                </Stack>
            </Drawer>
        </>
    )
}
